import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { Descriptions, Button, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

interface ContactAddress {
  id?: string;
  code?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  state?: string | null;
  city?: string | null;
  district?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
  address?: string | null;
  full_address?: string | null;
  default_sales_channel?: { name?: string | null } | null;
  default_sales_channel_id?: string | null;
  editable?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  [key: string]: unknown;
}

interface ContactAddressShowProps {
  contactAddress: ContactAddress | null | undefined;
  role?: 'admin' | 'operator';
}

const ContactAddressShow: React.FC<ContactAddressShowProps> = ({ contactAddress, role = 'admin' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!contactAddress) {
    return (
      <Empty description={t('common.error.no_data')} />
    );
  }

  const basePath = role === 'operator' ? '/operator' : '/admin';
  const lastItem = { label: contactAddress.code || contactAddress.receiver_name || '' };
  const breadcrumbItems = role === 'operator'
    ? ['merchants', 'merchants.contact_addresses', lastItem]
    : ['data', 'data.contact_addresses', lastItem];

  const headerActions = contactAddress.editable && (
    <Can requiredPermission="contact_addresses:write">
      <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`edit`)}>
        {t('common.button.edit')}
      </Button>
    </Can>
  );

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={breadcrumbItems}
        actions={headerActions}
      />
      <Descriptions bordered column={2}>
        <Descriptions.Item label={t(`models.contact_address.code`)}>
          {contactAddress.code || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.receiver_name`)}>
          {contactAddress.receiver_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.receiver_phone`)}>
          {contactAddress.receiver_phone || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.city`)}>
          {contactAddress.city || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.district`)}>
          {contactAddress.district || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.postal_code`)}>
          {contactAddress.postal_code || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.country_code`)}>
          {contactAddress.country_code || '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t(`models.contact_address.state`)}>
          {contactAddress.state || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.address`)} span={2}>
          {contactAddress.address || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.full_address`)} span={2}>
          {contactAddress.full_address || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.contact_address.default_channel`)} span={2}>
          {contactAddress.default_sales_channel ? contactAddress.default_sales_channel.name : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`common.created_at`)}>
          {contactAddress.created_at ? formatDateTime(contactAddress.created_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`common.updated_at`)}>
          {contactAddress.updated_at ? formatDateTime(contactAddress.updated_at) : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};
export default ContactAddressShow;
